// frontend/src/context/AuthContext.jsx

import axios from "axios";
import React, { createContext, useState, useEffect, useContext } from "react";
import { setSentryUser } from "../config/sentry";

export const AuthContext = createContext();

/**
 * Proveedor de contexto de autenticación.
 * Gestiona el estado del usuario autenticado, el token JWT y proporciona funciones
 * para login, logout y recarga de datos del usuario.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Componentes hijos
 * @returns {JSX.Element}
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("token");
    if (storedUser && storedToken) {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      setToken(storedToken);
      setSentryUser(parsed);
    }
    setLoading(false);
  }, []);

  const reloadUser = async () => {
    if (!token) return;
    try {
      const timestamp = new Date().getTime();
      const res = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL || "http://localhost:4000"}/api/v1/users/me?_t=${timestamp}`,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          } 
        }
      );
      setUser(res.data);
      localStorage.setItem("user", JSON.stringify(res.data));
      return res.data;
    } catch (err) {
      return null;
    }
  };

  const login = (userData, jwtToken, jwtRefreshToken) => {
    setUser(userData);
    setToken(jwtToken);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", jwtToken);
    if (jwtRefreshToken) {
      localStorage.setItem("refreshToken", jwtRefreshToken);
    }
    setSentryUser(userData);
  };

  const logout = async () => {
    // Invalidar refresh token en el servidor
    const refreshToken = localStorage.getItem("refreshToken");
    if (refreshToken) {
      try {
        await axios.post(
          `${import.meta.env.VITE_BACKEND_URL || "http://localhost:4000"}/api/v1/auth/logout`,
          { refreshToken }
        );
      } catch {
        // Ignorar errores de red al cerrar sesión
      }
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    setSentryUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, reloadUser }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
